/**
 * The two help.com lockups, inlined rather than loaded through `<img src>`: `currentColor` only
 * resolves inside inlined SVG, and the crops below are not expressible through an image element.
 * The vendored `src/assets/help-logo.svg` (the same geometry as the help.com prototype's
 * `logo.svg`) stays unmodified — the path data below is byte-identical to it, which
 * `src/__tests__/help-logo-parity.test.ts` pins.
 */

/** The four `help` wordmark letters, verbatim from the vendored asset. */
export const HELP_WORDMARK_PATHS = [
  "M65.3,128.5h119.4v159.4h120.6v-159.4h120v425.8h-120v-161.8h-120.6v161.7h-119.4s0-425.7,0-425.7Z",
  "M495.8,128.5h285.8v105.3h-167v62.9h156.4v87h-156.4v65.9h167v104.7h-285.8V128.5h0Z",
  "M852.1,128.5h119.4v321.1h134.1v104.7h-253.5V128.5h0Z",
  "M1176.2,128.5h131.1c129.7,0,212.3,46.2,212.3,165.3s-91.3,162.3-208.2,162.3h-15.9v98.2h-119.4V128.5h0ZM1330.8,354.3c38,0,67-23.5,67-62.3s-27.5-61.2-67-61.2h-35.3v123.5h35.3Z",
];

/** The `.com` set under `help`, verbatim from the vendored asset's `<g>`. */
export const HELP_DOT_COM_PATHS = [
  "M688.9,785.9c0-8.4,6.3-13.8,14.6-13.8s14.6,5.4,14.6,13.8-6.5,13.9-14.6,13.9-14.6-5.7-14.6-13.9Z",
  "M744.4,712.1c0-51.6,34.5-88.7,83.2-88.7s65.3,18.5,77.8,54.3l-20.6,5c-10.2-25.6-29-38.4-56.8-38.4s-60.6,27.7-60.6,67.8,25.1,67.8,60.6,67.8,46.9-13.8,56.4-38.4l20.6,5c-11.4,34.4-39.1,54.3-77.5,54.3s-83.2-37.1-83.2-88.7h0Z",
  "M928.6,712.1c0-61.5,41.8-88.7,84.5-88.7s85,27.1,85,88.7-41.8,89.2-84.5,89.2-85-27.1-85-89.2ZM1074.7,712.1c0-41.8-24.8-67.8-61.8-67.8s-61.1,25.6-61.1,67.8,24.5,68.3,61.8,68.3,61.1-26.3,61.1-68.3Z",
  "M1132.6,626.8h23.3v37.9c6-24.8,25.6-40.9,54-40.9s45.9,13.9,54.6,45.1c7.5-29.2,29.5-45.4,62.6-45.4s61.5,28.9,61.5,89.8v84.2h-23.5v-82.7c0-48.3-12.2-70-45.9-70s-48,21.7-48,70v82.7h-23.3v-82c0-49.3-10.4-70.4-43.6-70.4s-48.3,24.2-48.3,73.1v79.3h-23.3v-170.7h0Z",
];

/** The `"!"` mark: its dot and its stroke, verbatim from the vendored asset. */
export const HELP_MARK_CIRCLE = { cx: "1586.8", cy: "705.5", r: "122.5" };
export const HELP_MARK_POLYGON = "1574 38.4 1876.4 38.4 1706.7 554.3 1527.9 554.3 1574 38.4";

/** Brand blue. The `"!"` keeps it in both themes; the wordmark inherits `currentColor`. */
export const HELP_MARK_COLOR = "#2874ff";

/** Expanded lockup: the asset cropped to its ink — `help`, `.com` beneath it, and the `"!"`. */
export const HELP_LOGO_VIEWBOX = "65.3 38.4 1811.1 789.6";

/** Icon rail: the crop excludes `help` and `.com` by x, leaving the `"!"` alone. */
export const HELP_LOGO_MARK_VIEWBOX = "1464.3 38.4 412.1 789.6";

function HelpMark() {
  return (
    <>
      <circle fill={HELP_MARK_COLOR} {...HELP_MARK_CIRCLE} />
      <polygon fill={HELP_MARK_COLOR} points={HELP_MARK_POLYGON} />
    </>
  );
}

/**
 * `help!` with `.com` beneath — the sidebar's expanded `logo` slot. The wordmark and `.com` inherit
 * `--sidebar-foreground`. 40px tall (the prototype's top-bar logo height) so `.com` stays legible;
 * it fits the 52px sidebar header.
 */
export function HelpLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox={HELP_LOGO_VIEWBOX}
      width={92}
      height={40}
      fill="currentColor"
      role="img"
      aria-label="help.com"
      className={className}
    >
      {[...HELP_WORDMARK_PATHS, ...HELP_DOT_COM_PATHS].map((d) => (
        <path key={d} d={d} />
      ))}
      <HelpMark />
    </svg>
  );
}

/** The `"!"` alone — the collapsed 48px icon rail's `logoIcon` slot. */
export function HelpLogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox={HELP_LOGO_MARK_VIEWBOX}
      width={16}
      height={30}
      role="img"
      aria-label="help.com"
      className={className}
    >
      <HelpMark />
    </svg>
  );
}
